import {faArrowDownToBracket, faDownload} from "@fortawesome/pro-solid-svg-icons"
import {Grid, Typography, useTheme} from "@mui/material"
import Button from "@mui/material/Button"
import {DatePicker} from "@mui/x-date-pickers"
import {IApiFunc, IApiResult} from "auth/interface/api.interface"
import {useApi} from "auth/useApi"
import {AppContext} from "components/AppContextProvider"
import FormSelect from "components/Form/FormComponents/FormSelect/FormSelect"
import {StyledInputLabel} from "components/Form/FormComponents/FormSwitch/FormSwitch.styled"
import {Sidebar} from "components/sidedrawer/Sidebar2"
import {useFormikContext} from "formik"
import {apiEndpoints} from "generated/apiEndpoints"
import moment from "moment"
import {Deviation} from "pages/deviation/deviation.interface"
import {useContext, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {StyledFontAwesomeIconForButton} from "shared/styled/General"
import * as Yup from "yup"

const deviationExportValidationSchema = Yup.object().shape({
    startDate: Yup.string().required("Startdatum är obligatoriskt"),
    endDate: Yup.string().required("Slutdatum är obligatoriskt").required("Slutdatum är obligatoriskt"),
    deviationType: Yup.string().required("Typ av avvikelse är obligatoriskt")
})

interface DeviationExportDropdownProps {
    deviationData: Deviation[]
    isLoadingDeviations: boolean
}

interface DeviationExportValues {
    startDate: string
    endDate: string
    deviationType: string | number
}

const initialValues: DeviationExportValues = {
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
    deviationType: ""
}

export default function ExcelExportSidebar() {
    const [formikValues, setFormikValues] = useState(initialValues)
    const formRef = useRef() as any
    const [deviationResult, deviationApi] = useApi() as [IApiResult, IApiFunc]
    const appContext = useContext(AppContext)
    const {t} = useTranslation()
    const theme = useTheme()

    const handleDownload = async (values: DeviationExportValues) => {

        // Send the request to get the base64 encoded Excel data
        await deviationApi.getBlobFromPost(
            `${apiEndpoints.deviation.getdeviationexceldate}?deviationFormTemplateKey=${values.deviationType}&startDate=${values.startDate}&endDate=${values.endDate}&lang=${appContext.selectedLanguage}`, null,
            "EcoMobility_detailed_export.xlsx"
        )
    }

    const handleFormikSubmit = (values: DeviationExportValues) => {
        handleDownload(values)
    }

    const DateInterval = () => {
        const {values, touched, errors, setFieldValue} = useFormikContext<DeviationExportValues>()

        return (
            <>
                <Grid item xs={12} sx={{mb: 2}}>
                    <StyledInputLabel>
                        <Typography variant="h6" color="black">
                            {t("global.start_date", "Start date")}
                        </Typography>
                    </StyledInputLabel>
                    <DatePicker
                        value={values.startDate ? moment(values.startDate) : null}
                        format={"YYYY-MM-DD"}
                        onChange={(value) => {
                            setFieldValue("startDate", moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : null)
                        }}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                placeholder: t("global.start_date", "Start date"),
                                helperText: touched.startDate && errors.startDate ? errors.startDate : null,
                                error: touched.startDate && errors.startDate ? true : false,
                                sx: {width: "100%"}
                            }
                        }}
                    />
                </Grid>

                <Grid item xs={12} sx={{mb: 2}}>
                    <StyledInputLabel>
                        <Typography variant="h6" color="black">
                            {t("global.end_date", "End date")}
                        </Typography>
                    </StyledInputLabel>
                    <DatePicker
                        value={values.endDate ? moment(values.endDate) : null}
                        format={"YYYY-MM-DD"}
                        onChange={(value) => {
                            setFieldValue("endDate", moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : null)
                        }}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                placeholder: t("global.end_date", "End date"),
                                helperText: touched.endDate && errors.endDate ? errors.endDate : null,
                                error: touched.endDate && errors.endDate ? true : false,
                                sx: {width: "100%"}
                            }
                        }}
                    />
                </Grid>
            </>
        )
    }

    const DeviationTypeSelect = () => {
        const {values, touched, errors, setFieldValue} = useFormikContext<DeviationExportValues>()

        const options = [
            {label: t("global.features.title.deviationvehicledamage", "Vehicle damage"), value: "vehicle_damage"},
            {
                label: t("global.features.title.deviationthreatsandviolence", "Treath and violence"),
                value: "threats_and_violence"
            },
            {
                label: t("global.features.title.deviationworkingenvironment", "Working environment"),
                value: "working_environment"
            },
            {
                label: t("global.features.title.deviationtrafficandcustomerinfo", "Traffic and customer info"),
                value: "traffic_and_customerinfo"
            },
            {label: t("global.features.title.deviationtrafficlog", "Traffic log"), value: "traffic_log"},
            {label: t("global.features.title.deviationsuggestionbox", "Suggestion box"), value: "suggestion_box"},
            {label: t("global.school_buss_ride", "School bus"), value: "gotland_school_bus"}
        ]

        return (
            <>
                <Grid item xs={12} sx={{mb: 2}}>
                    <StyledInputLabel>
                        <Typography variant="h6" color="black">
                            {t("deviation.type_of_deviation", "Type of deviation")}
                        </Typography>
                    </StyledInputLabel>

                    <FormSelect
                        id="deviation-select"
                        labelKey="select.label"
                        labelDefault=""
                        optional={true}
                        options={options}
                        handleChange={(_, value) => setFieldValue("deviationType", value)}
                        value={options.find((option) => option.value === values.deviationType) || null}
                        helperText={touched.deviationType && errors.deviationType ? errors.deviationType : null}
                        error={touched.deviationType && errors.deviationType ? true : false}
                    />
                </Grid>
            </>
        )
    }

    return (
        <div>


            <Button variant="contained" color="primary" onClick={() => appContext.toggleSideBar(true)}>
                {t("global.detailed_export", "Detailed export")}
                <StyledFontAwesomeIconForButton
                    icon={faArrowDownToBracket}
                    color={theme.palette.secondary.main}
                />
            </Button>


            <Sidebar
                initialFormikValues={initialValues}
                setInitialFormikValues={setFormikValues}
                formResult={deviationResult}
                formData={{
                    formId: "DeviationExportForm",
                    subTitleNew: "",
                    subTitleEdit: "",
                    submitEditText: "",
                    submitText: t("global.buttons.download", "Download"),
                    icon: faDownload,
                    titleEdit: t("deviation.export_deviations", "Export deviations"),
                    titleNew: t("deviation.export_deviations", "Export deviations")
                }}
                handleSubmit={handleFormikSubmit}
                validationSchema={deviationExportValidationSchema}
                formRef={formRef}>
                <>
                    <DateInterval/>
                    <DeviationTypeSelect/>
                </>
            </Sidebar>
        </div>
    )
}
